import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRightLeft, Mail, MessageSquare, Send, Globe } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const ALL_CONTACT_TYPES = [
  { type: 'discord',  placeholder: 'YourName#1234 or discord.gg/...' },
  { type: 'telegram', placeholder: '@YourTelegram' },
  { type: 'email',    placeholder: 'contact@email.com' },
  { type: 'website',  placeholder: 'https://yoursite.com' },
];

export default function AccountSettings() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [newRole, setNewRole] = useState('');
  const [username, setUsername] = useState(user?.username || '');
  const [contacts, setContacts] = useState([{ type: 'discord', value: '' }]);
  const [saving, setSaving] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState(null);
  const checkTimeout = useRef(null);

  const checkUsername = (val) => {
    if (checkTimeout.current) clearTimeout(checkTimeout.current);
    if (!val || val.length < 3 || newRole !== 'seller') { setUsernameCheck(null); return; }
    setUsernameCheck('checking');
    checkTimeout.current = setTimeout(async () => {
      try {
        const { data } = await API.get(`/auth/check-username?username=${encodeURIComponent(val)}&role=seller`);
        setUsernameCheck(data.available ? 'available' : 'taken');
      } catch { setUsernameCheck(null); }
    }, 400);
  };

  const updateContact = (type, val) => {
    setContacts(prev => {
      const idx = prev.findIndex(c => c.type === type);
      if (idx >= 0) {
        const updated = [...prev];
        if (!val.trim()) updated.splice(idx, 1);
        else updated[idx] = { type, value: val };
        return updated.length ? updated : [{ type: 'discord', value: '' }];
      }
      return val.trim() ? [...prev, { type, value: val }] : prev;
    });
  };

  const handleChangeRole = async () => {
    if (!newRole || newRole === user?.role) return;
    if (newRole === 'seller') {
      if (!username.trim() || username.trim().length < 3) return toast.error('Username must be at least 3 characters');
      if (usernameCheck === 'taken') return toast.error('Username already taken');
      const validContacts = contacts.filter(c => c.value.trim());
      if (validContacts.length === 0) return toast.error('Sellers must provide at least one contact method');
    }
    setSaving(true);
    try {
      const { data } = await API.post('/auth/change-role', {
        new_role: newRole,
        username: newRole === 'seller' ? username.trim() : undefined,
        contact_methods: newRole === 'seller' ? contacts.filter(c => c.value.trim()) : [],
      });
      updateUser(data.user);
      toast.success(data.message);
      if (newRole === 'seller') navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change account type');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-rm-bg">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="font-rajdhani font-bold text-3xl uppercase mb-2">
          Account <span className="text-rm-red">Settings</span>
        </h1>
        <p className="text-rm-muted text-sm mb-8">Manage your account type and preferences.</p>

        {/* Current Status */}
        <div className="bg-rm-card border border-rm-border rounded-sm p-5 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-rm-muted uppercase tracking-widest mb-1">Current Account</p>
              <p className="text-lg font-rajdhani font-bold text-white">{user?.username}</p>
              <p className="text-sm text-rm-muted">{user?.email}</p>
            </div>
            <span className={`px-3 py-1.5 border rounded-sm font-rajdhani font-bold uppercase text-sm
              ${user?.role === 'seller' ? 'border-rm-red text-rm-red bg-rm-red/10' : 'border-blue-500/50 text-blue-400 bg-blue-500/10'}`}>
              {user?.role}
            </span>
          </div>
        </div>

        {/* Role Change Card */}
        <div className="bg-rm-card border border-rm-border rounded-sm p-6">
          <h2 className="font-rajdhani font-bold text-xl uppercase flex items-center gap-2 mb-5">
            <ArrowRightLeft size={18} className="text-rm-red" /> Change Account Type
          </h2>

          <div className="grid grid-cols-2 gap-3 mb-5">
            {['buyer', 'seller'].map(r => (
              <button key={r} type="button"
                onClick={() => { setNewRole(r); checkUsername(username, r); }}
                disabled={r === user?.role}
                data-testid={`change-role-${r}`}
                className={`py-4 border rounded-sm font-rajdhani font-bold uppercase text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed
                  ${newRole === r
                    ? 'border-rm-red text-rm-red bg-rm-red/10'
                    : r === user?.role
                      ? 'border-rm-border text-rm-muted cursor-not-allowed'
                      : 'border-rm-border text-rm-muted hover:border-white/20 hover:text-white'}`}>
                {r === 'buyer' ? 'Buyer' : 'Seller'}
                {r === user?.role && (
                  <div className="text-[9px] text-rm-muted mt-0.5 normal-case tracking-normal">Current</div>
                )}
              </button>
            ))}
          </div>

          {newRole && newRole !== user?.role && (
            <div className="space-y-5 border-t border-rm-border pt-5">
              {newRole === 'seller' && (
                <>
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-4">
                    <p className="text-sm text-amber-400 font-bold mb-1">Becoming a Seller</p>
                    <ul className="text-xs text-zinc-500 space-y-1 list-disc list-inside">
                      <li>Choose a unique username (your seller profile URL)</li>
                      <li>Provide contact methods for buyers to reach you</li>
                      <li>You can switch back to buyer anytime</li>
                    </ul>
                  </div>
                  {/* Username */}
                  <div>
                    <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">
                      Seller Username <span className="text-rm-red">*</span>
                    </label>
                    <div className="relative">
                      <input type="text" value={username}
                        onChange={e => { setUsername(e.target.value); checkUsername(e.target.value); }}
                        placeholder="your_unique_username"
                        data-testid="seller-username-input"
                        className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-3 rounded-sm text-sm pr-10" />
                      <div className="absolute right-3 top-3">
                        {usernameCheck === 'checking' && <div className="w-4 h-4 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" />}
                        {usernameCheck === 'available' && <Check size={15} className="text-green-500" />}
                        {usernameCheck === 'taken' && <X size={15} className="text-rm-red" />}
                      </div>
                    </div>
                    {usernameCheck === 'taken' && (
                      <p className="text-[11px] text-rm-red mt-1">Username already taken by another seller. Choose a different one.</p>
                    )}
                    {usernameCheck === 'available' && (
                      <p className="text-[11px] text-green-500 mt-1">Username available! Your profile will be at robloxmart.store/{username}</p>
                    )}
                    <p className="text-[10px] text-zinc-600 mt-1">This becomes your public seller profile URL</p>
                  </div>

                  {/* Contacts */}
                  <div>
                    <label className="text-xs uppercase tracking-widest text-rm-muted block mb-1">Contact Methods</label>
                    <p className="text-[11px] text-amber-500/80 mb-3">At least one required — buyers use this to reach you</p>
                    <div className="space-y-2">
                      {ALL_CONTACT_TYPES.map(({ type, placeholder }) => (
                        <div key={type} className="flex items-center gap-3">
                          <span className="text-xs text-rm-muted capitalize w-20 shrink-0">{type}</span>
                          <input type="text" placeholder={placeholder}
                            value={contacts.find(c => c.type === type)?.value || ''}
                            onChange={e => updateContact(type, e.target.value)}
                            data-testid={`contact-${type}`}
                            className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-xs" />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {newRole === 'buyer' && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-sm p-4">
                  <p className="text-sm text-blue-400 font-bold mb-1">Switching to Buyer</p>
                  <p className="text-xs text-zinc-500">Your existing listings will be deactivated. You can switch back to seller anytime and reactivate them.</p>
                </div>
              )}

              <button onClick={handleChangeRole}
                disabled={saving || (newRole === 'seller' && usernameCheck === 'taken')}
                data-testid="confirm-role-change"
                className="w-full btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
                {saving ? 'Updating...' : `Switch to ${newRole === 'buyer' ? 'Buyer' : 'Seller'}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
